import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserInfoStore } from "../store";
import { confirmUserReq, getTenantNameByPhoneReq } from "@/api/services/auth";
import type { UserInfoFormData } from "../store/types";
import { paths } from "@/navigation/paths";
import { login } from "@/auth/login";

type FieldErrors = Partial<Record<keyof UserInfoFormData, string>>;

export const useUserInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fieldsErrors, setFieldsErrors] = useState<FieldErrors>({});

  const {
    name,
    surname,
    password,
    confirmPassword,
    setName,
    setSurname,
    setPassword,
    setConfirmPassword,
    setLoading,
    setError,
    isLoading,
    errorMessage,
  } = useUserInfoStore();

  const phone = location.state?.phone || "";
  const id = location.state?.id || null;

  // Clear error when component mounts
  useEffect(() => {
    setError(null);
  }, [setError]);

  useEffect(() => {
    if (!phone || !id) {
      navigate(paths.SIGN_UP);
    }
  }, [phone, id, navigate]);

  // Load tenant name if available
  useEffect(() => {
    const loadTenantName = async () => {
      try {
        const response = await getTenantNameByPhoneReq(phone);
        if (response.data?.name && response.data?.surname) {
          setName(response.data.name);
          setSurname(response.data.surname);
        }
      } catch (error) {
        // Ignore error - user will fill in manually
      }
    };

    if (phone) {
      loadTenantName();
    }
  }, [phone, setName, setSurname]);

  const formData: UserInfoFormData = {
    name,
    surname,
    password,
    confirmPassword,
  };

  const onChangeField = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof UserInfoFormData
  ) => {
    const value = e.target.value;
    switch (field) {
      case "name":
        setName(value);
        break;
      case "surname":
        setSurname(value);
        break;
      case "password":
        setPassword(value);
        break;
      case "confirmPassword":
        setConfirmPassword(value);
        break;
    }

    if (fieldsErrors[field]) {
      setFieldsErrors({ ...fieldsErrors, [field]: undefined });
    }
  };

  const onSubmit = async (data: UserInfoFormData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await confirmUserReq(id, {
        name: data.name,
        surname: data.surname,
        password: data.password,
        phone: phone,
      });

      if (response.data?.token && response.data?.refreshToken) {
        // Store tokens and login
        login(response.data.token, response.data.refreshToken);
        // Clear the form after successful registration
        useUserInfoStore.getState().reset();
        // Navigate to user bookings
        navigate(paths.USER_BOOKINGS);
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Registration failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const setFieldErrorsHandler = (errors: FieldErrors) => {
    setFieldsErrors(errors);
  };

  const clearFieldErrorsHandler = () => {
    setFieldsErrors({});
  };

  return {
    formData,
    fieldsErrors,
    onSubmit,
    onChangeField,
    setFieldErrorsHandler,
    clearFieldErrorsHandler,
    isLoading,
    errorMessage,
  };
};
